import resolve from '../resolver';
import authorizedFetch from '../authorizedFetch';
import { idToName } from '@helpers/naming';
import productsMap from '@services/product/productsMap';

export interface Product {
  id: string;
  name: string;
  versions?: string[];
  shared?: string[];
}

interface ProductsResponse {
  products: string[];
}

interface VersionsResponse {
  product: string;
  versions: string[];
  shared?: string[];
}

export const list = async (): Promise<Product[]> => {
  try {
    const res = await authorizedFetch(resolve('ota/products'));
    const json: ProductsResponse = await res.json();
    return json.products.map((id: string) => ({
      id,
      name: productsMap[id] ?? idToName(id)
    }));
  } catch (e: unknown) {
    return [];
  }
};

export const getVersionsByProduct = async (productId: string): Promise<string[]> => {
  try {
    const res = await authorizedFetch(resolve('ota/product-versions/' + productId));
    const json: VersionsResponse = await res.json();
    return json.versions;
  } catch (e: unknown) {
    return [];
  }
};

export const getVersionsByProducts = async (productIds: string[]): Promise<VersionsResponse[]> => {
  try {
    const query = new URLSearchParams();

    productIds.forEach((id) => query.append('product_names', id));

    const res = await authorizedFetch(resolve(`ota/product-versions?${query.toString()}`));

    const json = await res.json();
    return json.products;
  } catch (e: unknown) {
    return [];
  }
};

export const getProductsWithVersions = async (): Promise<Product[]> => {
  try {
    const products = await list();
    const ids = products.map((p) => p.id);
    const response = await getVersionsByProducts(ids);
    const map = new Map<string, VersionsResponse>();
    response.forEach((p) => {
      map.set(p.product, p);
    });
    for (const product of products) {
      const res = map.get(product.id);
      if (!res) {
        continue;
      }
      product.versions = res.versions;
      if (res.shared) {
        product.shared = res.shared;
      }
    }
    return products;
  } catch (e: unknown) {
    return [];
  }
};
